import { sumBy } from 'lodash'
import { OrderSummaryProps } from '@scentregroup/shared/components/order-summary'
import { IMarketplaceCartItem } from '../types/cart'
import {
  calculateDeliveryFees,
  displayFulfilmentOptionString,
} from '../lib/marketplace-delivery'
import {
  IMarketplaceCheckout,
  IMarketplaceFulfilmentOption,
  IMarketplaceParcel,
  MarketplaceFulfilmentType,
} from '../types/marketplace-checkout'
import { IParcel, IMarketplaceItem } from '../types/order'

export const calculateItemCountFromItems = (
  items: (IMarketplaceCartItem | IMarketplaceItem)[]
): number => {
  return items.reduce(
    (acc: number, item: IMarketplaceCartItem | IMarketplaceItem) => {
      acc += item.quantity
      return acc
    },
    0
  )
}

export const calculateItemCountFromParcels = (
  parcels: (IMarketplaceParcel | IParcel)[]
): number =>
  parcels.reduce((acc: number, parcel: IMarketplaceParcel | IParcel) => {
    return acc + calculateItemCountFromItems(parcel.items)
  }, 0)

export const calculateSumWithDiscount = (
  parcels: IMarketplaceCartItem[]
): number =>
  sumBy(
    parcels,
    ({ discountPrice, listPrice, quantity }) =>
      (discountPrice || listPrice) * quantity
  )

export const calculateSumWithNoDiscount = (
  parcels: IMarketplaceCartItem[]
): number => sumBy(parcels, ({ listPrice, quantity }) => listPrice * quantity)

export const calculateTotalDiscount = (
  parcels: IMarketplaceCartItem[],
  cartDiscount?: number
): number => {
  const itemDiscounts = parcels.reduce(
    (acc, { listPrice, discountPrice, quantity }) =>
      acc + (listPrice - (discountPrice ?? listPrice)) * quantity,
    0
  )
  return itemDiscounts + (cartDiscount ?? 0)
}

export const checkoutToOrderSummary = (
  checkout: IMarketplaceCheckout
): OrderSummaryProps => {
  const subTotal = sumBy(checkout.parcels, parcel =>
    calculateSumWithNoDiscount(parcel.items)
  )

  const deliveryFees = calculateDeliveryFees(checkout.parcels)
  const itemCount = calculateItemCountFromParcels(checkout.parcels)

  return {
    itemCount: itemCount,
    fees: deliveryFees,
    subTotal: subTotal,
    total: checkout.total,
    displaySubTotalText: false,
  }
}

export const checkoutFormToOrderSummary = (
  checkout: IMarketplaceCheckout,
  preferredFulfilmentType: MarketplaceFulfilmentType | undefined
): OrderSummaryProps => {
  const checkoutFulfilmentOption = checkout.fulfilmentOptions.find(
    o => o.fulfilmentType === checkout.preferredFulfilmentType
  )
  const subTotal = sumBy(checkout.parcels, parcel =>
    calculateSumWithNoDiscount(parcel.items)
  )

  const selectedFulfilmentOption = checkout.fulfilmentOptions.find(
    ({ fulfilmentType }) => fulfilmentType === preferredFulfilmentType
  )

  const deliveryFees = checkoutFormDeliveryFees(selectedFulfilmentOption)
  const deliveryCost = deliveryFees.reduce(
    (acc, df) => acc + (df?.price ?? 0),
    0
  )
  const itemCount = calculateItemCountFromParcels(checkout.parcels)

  // subtract cost of previously-selected fulfiment option before adding cost of current selection
  const total =
    checkout.total - (checkoutFulfilmentOption?.cost ?? 0) + deliveryCost

  return {
    itemCount: itemCount,
    fees: deliveryFees,
    subTotal: subTotal,
    total,
    displaySubTotalText: false,
  }
}

function checkoutFormDeliveryFees(
  fulfilmentOption: IMarketplaceFulfilmentOption | undefined
): Exclude<OrderSummaryProps['fees'], undefined> {
  if (!fulfilmentOption) {
    return []
  }

  return [
    {
      name: displayFulfilmentOptionString(fulfilmentOption.fulfilmentType),
      price: fulfilmentOption.cost,
    },
  ]
}
